import type { Messages } from '../messages.js'

/**
 * Catalan. `jugada` for flips, the word a Catalan board game uses for a move.
 *
 * Written in the standard of the Institut d'Estudis Catalans. The one place it shows is the
 * ela geminada, which the interface has no occasion to use but the tiles do: COL·LEGI is played
 * as two Ls, since the interpunct is punctuation rather than a letter.
 */
export const ca: Messages = {
  tag: 'ca',

  readingDictionary: 'S’està llegint el diccionari…',
  noWordList: 'No hi ha llista de mots per a «{language}». Creeu-ne una:  pnpm dictionary build',
  emptyWordList: 'La llista de mots per a «{language}» és buida.',

  flips: 'jugades',
  score: 'punts',
  words: 'mots',
  round: 'ronda',
  ticksLeftLabel: 'Temps que queda en aquesta ronda',
  typeAWord: 'escriviu un mot',
  tapPrompt: 'toqueu lletres per triar-les o tornar-les, i després {action}',

  boardOfTiles: 'Tauler de {n} fitxes',
  faceDown: 'de cara avall',
  wildCard: 'escarràs',
  wildKey: 'qualsevol lletra',
  letterReplaced: '{from} ha esdevingut {to}',
  letterSwap: 'CANVI DE LLETRES!',
  spentTile: 'fitxa gastada',
  hiddenWhilePaused: 'amagat mentre està en pausa',
  letterInWord: '{letter}, lletra {position} del mot',

  completeWord: 'Completa el mot',

  completeShort: 'Completa',
  reset: 'Neteja',
  pause: 'Pausa',
  resume: 'Reprèn',
  newGame: 'Partida nova',
  paused: 'En pausa',
  outOfFlips: 'Sense jugades',
  finalResult: '{score} per {words} en {rounds}',
  playAgain: 'Torna-hi',
  share: 'Comparteix',
  shareCopied: 'Copiat.',
  shareSelect: 'Copieu això:',

  lettersSelect: 'les lletres trien',
  keysWild: 'es pren quan escriviu una lletra que cap fitxa no mostra',
  clearsEvery: 'neteja totes les {letter} triades',
  undoLastLetter: 'desfés l’última lletra',
  noWordsYet: 'Encara cap mot.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'ja trobat',
  reasonTooShort: 'massa curt',
  reasonNotAWord: 'no és un mot',
  reasonAllFound: 'ja els teniu tots',
  noSuchLetterUp: 'cap {letter} destapada',
  nothingUp: 'res destapat',
  letterHidden: 'una lletra s’ha ocultat',
  shuffled: 'barrejat',
  shuffledAndBilled: 'barrejat, s’han cobrat {flips} sense usar',
  tooFewLetters: 'Queden massa poques lletres',

  gameLanguage: 'llengua',
  interfaceLanguage: 'interfície',
  dictionarySize: '{common} d’ús corrent de {full} mots',
  filterLanguages: 'Cerca una llengua',
  noMatches: 'Cap coincidència',

  nerdMode: 'mode per a entesos',
  rules: 'Regles',
  difficulty: 'dificultat',
  difficultyNames: { easy: 'fàcil', medium: 'mitjana', hard: 'difícil', insane: 'demencial' },
  pausedNoBoard: 'Aquesta partida no sortirà a la classificació.',
  about: 'Quant a',
  themeLabel: 'tema',
  themeNames: { traditional: 'tradicional', light: 'clar', contrast: 'alt contrast' },
  tiles: 'fitxes (N)',
  secondsPerTick: 'segons / batec',
  holdTicks: 'batecs d’aturada',
  minWord: 'mot més curt',
  startingFlips: 'jugades inicials',
  wildChance: 'probabilitat d’escarràs',
  replaceChance: 'probabilitat de canvi de lletra',
  hideChance: 'probabilitat d’ocultació de lletra',
  wordCompleteMode: 'en completar un mot',
  wordCompleteNames: { shuffle: 'barreja', spend: 'gasta', keep: 'conserva' },
  flipEconomy: 'retorn de jugades',
  flipEconomyNames: {
    none: 'cap',
    perLetter: 'per lletra',
    fibonacci: 'fibonacci',
    overMinimum: 'sobre el mínim',
  },
  repeatedLetterKey: 'tecla de lletra repetida',
  keySchemeNames: { cycle: 'cicle', advance: 'avança' },
  keySchemeHelp: {
    cycle:
      'A pren la següent A sense usar, i quan ja hi són totes al mot, les neteja. ' +
      'Maj+A també les neteja.',
    advance: 'A pren la següent A sense usar. Maj+A neteja totes les A del mot.',
  },

  whatThatMeans: 'Què vol dir això',
  factRound: 'ronda',
  factWholeBoardUp: 'tot el tauler destapat durant',
  factRoundCosts: 'una ronda costa',
  factFlipsBuy: 'les jugades inicials compren',
  factThisBoard: 'aquest tauler',
  factBoardHadToAdmit: 'el tauler havia d’admetre',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, el més llarg {longest}',
  wordsIncludingOneOf: '{words}, un dels quals de {ceiling}',
  scorelessRounds: '{rounds} sense punts',

  whatAWordPays: 'Què paga un mot',
  columnLetters: 'lletres',
  columnCost: 'cost',
  columnPoints: 'punts',
  columnFlips: 'jugades',
  columnNet: 'net',

  canonicalRules: 'Regles canòniques de {difficulty}.',
  customRules: 'Canviat respecte del predefinit. Els punts amb regles pròpies no es classifiquen.',
  applyAndStart: 'Aplica i comença una partida nova',
  changesNextGame: 'Els canvis s’apliquen a la partida següent.',
  presets: 'Predefinits:',

  start: 'Comença',
  restart: 'Reinicia',
  quit: 'Deixa-ho',
  quitTitle: 'Voleu deixar aquesta partida?',
  restartTitle: 'Voleu reiniciar aquesta partida?',
  restartConfirm: 'Reinicia',
  quitConfirm: 'Deixa-ho',
  keepPlaying: 'Continua jugant',
  personalBest: 'Les vostres millors partides',
  newPersonalBest: 'Un rècord personal nou.',
  columnRank: '#',
  notRanked: 'Regles pròpies, així que aquesta partida no es classifica.',
  notRankedPaused: 'El rellotge s’ha aturat, així que aquesta partida no es classifica.',
  rankOfTotal: '{rank} de {total}',

  howToPlay: 'Com s’hi juga',

  backToGame: 'Torna a la partida',
  welcomeTitle: 'Us donem la benvinguda a Blinkered',
  tutorialSkip: 'Omet',
  tutorialNext: 'Següent',
  tutorialBack: 'Enrere',
  tutorialStart: 'Comença a jugar',
  tutorialHideAgain: 'No ho tornis a mostrar',
  tutorialProgress: '{n} de {total}',
  tutorialSkipTitle: 'Voleu ometre la presentació?',
  tutGoalTitle: 'Blinkered és una cursa',
  tutGoalBody:
    'Comenceu la partida amb un nombre fix de jugades, i cada fitxa que es gira costa una jugada. Quan s’acaben les jugades, la partida s’acaba. Formar mots dona jugades, i com més llarg és el mot, més jugades guanyeu.',
  tutSpentBody:
    'Les lletres que feu servir en un mot queden tapades fins a la ronda següent. Només un canvi de lletra altera quines lletres hi ha al tauler.',
  tutChangeTitle: 'Les lletres poden canviar',
  tutSameLetters:
    'Heu vist un mot que no heu arribat a jugar? Les mateixes lletres tornen a la ronda següent, barrejades en llocs nous.',
  tutPickLetters: 'Toqueu les lletres que voleu, en ordre, per formar un mot.',
  tutMoreTurn: 'Les fitxes continuen girant mentre penseu, i encara pot venir una lletra millor.',
  tutTapBack:
    'N’heu pres una que no volíeu? Torneu-la a tocar per retornar-la. Qualsevol, no només l’última.',
  tutComplete: 'Premeu Completa quan el mot sigui a punt.',
  htBoardTitle: 'El tauler',
  htBoardBody:
    'Les fitxes es giren d’una en una, en un ordre aleatori. Amb les destapades es formen mots.',
  htWordsTitle: 'Els mots',
  htWordsBody: 'Formeu un mot amb les fitxes destapades escrivint o clicant les lletres en ordre.',
  htFlipsTitle: 'Les jugades',
  htFlipsBody:
    'Cada fitxa que es gira costa una jugada. Un mot completat en retorna, i els mots llargs en retornen més. Quan s’acaben, la partida s’acaba.',
  htRoundTitle: 'La ronda',
  htRoundBody:
    'Quan es gira l’última fitxa de la ronda, tot el tauler s’atura un moment. Després les fitxes es giren i es barregen, i comença una ronda nova.',
  htLanguagesTitle: 'Les llengües',
  htLanguagesBody:
    'N’hi ha {n}. Tot tauler es pot resoldre amb mots que la gent fa servir de debò. Un mot poc corrent també puntua, si el diccionari el coneix.',
  htKeysTitle: 'El teclat',
  htWildTitle: 'Els escarrassos',
  htWildBody:
    'De tant en tant surt un escarràs en lloc d’una lletra. L’escarràs val per qualsevol lletra que faci un mot vàlid. Un mot ja completat no compta.',
  htHideTitle: 'Lletres que s’oculten',
  htHideBody:
    'En els nivells més difícils una lletra visible es pot tornar a girar sola i tornar més tard dins la ronda. El temporitzador guanya un tic per això, en vermell. Les lletres ja triades no es giren.',
  tutHideBody:
    'En els nivells més difícils una lletra visible es pot tornar a girar sola i tornar més tard dins la ronda. Les lletres ja triades no es giren.',
  htSwapTitle: 'Lletres que canvien',
  htSwapBody:
    'De tant en tant, entre rondes, una lletra és substituïda per una altra. Veureu quina ha marxat i quina ha arribat.',
  htLevelsTitle: 'Els nivells',
  htLevelEasy:
    'Les mateixes dotze lletres tota la partida, de manera que us les podeu aprendre i portar una llista de mots al cap. Les fitxes es giren a poc a poc, i el tauler sencer es veu prou estona per acabar la tria.',
  htLevelMedium:
    'De tant en tant una lletra canvia, i costa més recordar els mots que guardàveu per a més tard. Menys temps per mirar i menys per pensar.',
  htLevelHard:
    'Els mots de tres lletres deixen de comptar, i una lletra canvia gairebé cada dues rondes. El tauler tot just s’ensenya i ja es barreja.',
  htLevelInsane:
    'Tot alhora i a tota velocitat. El tauler es barreja gairebé just després de l’última jugada.',
  htTouchTitle: 'La pantalla tàctil',
  htTouchBody:
    'Toqueu una fitxa destapada per prendre’n la lletra. Toqueu una lletra presa per retornar-la. Completa i Neteja són sota el tauler.',

  accountTitle: 'El teu compte',
  menuProfile: 'El meu perfil',
  menuGames: 'Les meves partides',
  menuPublicPage: 'La meva pàgina pública',
  menuSignedInAs: 'Sessió iniciada com a',
  signIn: 'Inicia la sessió',
  signOut: 'Tanca la sessió',
  signInTitle: "Inicia la sessió o registra't",
  signInLead: 'Un compte guarda les teves partides, a tots els dispositius.',
  signInKeepGame: 'Inicia la sessió per guardar aquesta partida, i totes les següents.',
  continueWithApple: 'Continua amb Apple',
  continueWithGoogle: 'Continua amb Google',
  signInOr: 'o',
  emailLabel: 'Correu',
  codeLabel: 'Codi',
  emailMeACode: 'Envieu-me un codi',
  working: 'Un moment…',
  sendNewCode: 'Envia un codi nou',
  codeSent: 'Mira el correu. El codi dura 10 minuts.',
  notNow: 'Ara no',
  badEmail: 'Això no sembla una adreça de correu.',
  badCode:
    'Aquest codi no ha funcionat. Els codis caduquen als 10 minuts i només serveixen un cop.',
  serverBusy: "No s'ha pogut contactar amb el servidor. Torna-ho a provar d'aquí a un moment.",
  serverDown: "No s'ha pogut contactar amb el servidor.",
  offline: 'Fora de línia',
  leaderboardTitle: 'Classificació',
  leaderboardEmpty: 'Encara no hi ha partides en aquesta classificació. Sigues el primer.',
  leaderboardWouldBe: 'On quedaria aquesta partida',
  leaderboardThisGame: 'Aquesta partida',
  leaderboardRanksHere: 'On queda aquesta partida',
  leaderboardOutside: 'Fora de les cinc primeres.',
  tutAccountTitle: 'Guarda les teves puntuacions',
  tutAccountBody:
    'Un compte guarda les teves partides, posa les teves puntuacions a la classificació i manté el teu perfil.',
  ssoCancelled: "S'ha cancel·lat l'inici de sessió.",
  ssoExpired: 'Ha trigat massa. Torna a iniciar la sessió.',
  ssoNoUsername: "No s'ha pogut acabar de crear el compte. Torna-ho a provar d'aquí a un moment.",
  ssoFailed: 'No ha funcionat. Torna-ho a provar, o fes servir el correu.',
  tabProfile: 'Perfil',
  countryAny: 'Prefereixo no dir-ho',
  findCountry: 'Cerca països',
  usernameLabel: "Nom d'usuari",
  bioLabel: 'Sobre mi',
  countryLabel: 'País',
  bioHint: 'Sense enllaços. {left}/{max}',
  save: 'Desa',
  saving: "S'està desant…",
  saved: 'Desat.',
  nameTaken: 'Aquest nom ja el té algú.',
  nameTooShort: 'Una mica més llarg, si us plau.',
  nameTooLong: 'És massa llarg.',
  nameBadCharacters: 'Només lletres, xifres, guions i guions baixos.',
  nameBadEdges: 'Ha de començar i acabar amb una lletra o una xifra.',
  nameMixedScripts: "Tria un sol alfabet i queda-t'hi.",
  nameReserved: "Sembla un nom dels que donem nosaltres. Tria'n un altre.",
  nameUnusable: 'Aquest nom no es pot fer servir.',
  bioTooLong: 'Aquest camp admet {max} caràcters com a màxim.',
  bioHasLink: "Aquí no s'hi admeten enllaços.",
  bioHasControl: 'Conté caràcters que aquí no hi caben.',
  bioUnusable: 'Aquest text no es pot fer servir.',
  gamesHeading: 'Partides',
  gamesEmpty:
    'Aquí encara no hi ha res. Les partides que juguis amb la sessió iniciada sortiran aquí.',
  gamesEmptyShort: 'Aquí encara no hi ha res.',
  gamesLoading: "S'estan llegint les teves partides…",
  theirGamesLoading: "S'estan llegint les seves partides…",
  profileLoading: "S'està llegint el perfil…",
  playerNotFound: 'Aquí no hi ha ningú amb aquest nom.',
  columnWhen: 'Quan',
  columnRounds: 'rondes',
  columnGame: 'Partida',
  gameLoading: "S'està llegint la partida…",
  gameUnreadable: "No s'ha pogut llegir aquesta partida.",
  gameNotRanked: 'Jugada amb regles modificades, així que no es classifica.',
  detailNotKept: "Les paraules i els taulers d'aquesta partida ja no es guarden.",
  openingBoard: 'El tauler inicial',
  roundByRound: 'Ronda a ronda',
  roundNothing: "no s'ha trobat res",
  changeReplaced: '{letter} substituïda a la casella {slot}',
  changeWildOn: '{letter} comodí a la casella {slot}',
  changeWildOff: '{letter} comodí retirat de la casella {slot}',
  keepThisGame: 'Guarda aquesta partida',
  savedToYourGames: 'Desada a les teves partides.',

  reportThis: 'Denuncia',
  reportHeading: 'Denuncia això',
  reportTheName: 'El nom',
  reportTheBio: 'La descripció',
  reportTheScore: 'La puntuació',
  reportWhy: 'Què hi ha de dolent? Opcional.',
  reportSend: 'Envia la denúncia',
  reportSent: 'Gràcies. Algú s’ho mirarà.',
  reportSignIn: 'Inicia la sessió per denunciar alguna cosa.',
  reportGone: 'Això ja no hi és.',
  reportCancel: 'Cancel·la',

  deleteAccount: 'Suprimeix el meu compte',
  deleteAccountWhat:
    'Això elimina el teu perfil i totes les partides que has guardat, per sempre. No es pot desfer.',
  deleteAccountAsk: 'Envia’m un codi',
  deleteConfirm: 'Suprimeix-ho per sempre',
  deleteGone: 'El teu compte ja no hi és. Gràcies per jugar.',
  deleteNoAddress:
    'En aquest compte no hi ha cap adreça confirmada, així que no es pot verificar la supressió. Posa’t en contacte i ho farem a mà.',

  rulesetFacts: 'Mots de {letters} · una fitxa nova cada {seconds}',
  plurals: {
    words: { one: '{n} mot', other: '{n} mots' },
    rounds: { one: '{n} ronda', other: '{n} rondes' },
    flips: { one: '{n} jugada', other: '{n} jugades' },
    ticks: { one: '{n} batec', other: '{n} batecs' },
    points: { one: '{n} punt', other: '{n} punts' },
    minLetters: { one: '{n} lletra o més', other: '{n} lletres o més' },
    seconds: { one: '{n} segon', other: '{n} segons' },
  },
}
